import { useEffect, useState } from "react";
import Button from "@/components/base/Button";

type QuizOption = { label: string; type: string };
type QuizQuestion = { question: string; options: QuizOption[] };

const QUESTIONS: QuizQuestion[] = [
  {
    question: "回顧你最近卡住的目標，哪個描述最貼近？",
    options: [
      { label: "我不確定這是不是我真正想追求的", type: "方向型" },
      { label: "我還沒開始，就先覺得自己做不好", type: "信念型" },
      { label: "我想了很多，卻一直沒有真正開始", type: "啟動型" },
      { label: "我開始了，但過沒多久就停下來", type: "持續型" },
      { label: "我想做，但發現自己還缺方法或能力", type: "能力型" },
    ],
  },
  {
    question: "讓你最困擾的時刻，通常是？",
    options: [
      { label: "每天很忙，卻不知道自己在往哪裡走", type: "方向型" },
      { label: "明明很努力，卻常懷疑自己值不值得", type: "信念型" },
      { label: "心裡想做，行動上卻一直拖延", type: "啟動型" },
      { label: "好不容易開始，卻無法持續下去", type: "持續型" },
      { label: "遇到不會的事，不知道該從哪裡學", type: "能力型" },
    ],
  },
  {
    question: "如果可以改變一件事，你最希望是？",
    options: [
      { label: "找到真正想要的方向", type: "方向型" },
      { label: "對自己有更多信心", type: "信念型" },
      { label: "想做的時候就能立刻開始", type: "啟動型" },
      { label: "開始之後能堅持到底", type: "持續型" },
      { label: "擁有把事做好的能力", type: "能力型" },
    ],
  },
  {
    question: "現在開始一件新事情時，你第一個念頭通常是？",
    options: [
      { label: "這真的是我想要的嗎？", type: "方向型" },
      { label: "我怕自己做不好", type: "信念型" },
      { label: "等我想清楚再開始", type: "啟動型" },
      { label: "不知道這次能不能撐下去", type: "持續型" },
      { label: "我好像還不太會做這件事", type: "能力型" },
    ],
  },
];

const RESULTS: Record<string, { title: string; desc: string; action: string }> = {
  方向型: {
    title: "方向型卡點",
    desc: "你可能不是缺少努力，而是還不清楚自己真正想要什麼、該往哪裡走。當方向模糊時，行動自然難以持續，也容易一直更換目標。",
    action: "今天可以試著寫下：如果完全沒有顧慮，我最想過的生活長什麼樣子？",
  },
  信念型: {
    title: "信念型卡點",
    desc: "你很容易在開始前就先懷疑自己能不能做到，這些想法會讓行動卡在起點。看見這些自動運作的想法，是改變的第一步。",
    action: "今天可以試著把「我做不到」改寫成「我還在學習怎麼做」。",
  },
  啟動型: {
    title: "啟動型卡點",
    desc: "你想得很多，卻常常無法真正開始。問題不一定在意志力，而是少了讓第一步變得夠小、夠具體的方法。",
    action: "今天可以挑一件最想做的事，把它縮小到 5 分鐘內就能開始的第一個動作。",
  },
  持續型: {
    title: "持續型卡點",
    desc: "你可以開始，但不容易維持。關鍵往往不是更用力，而是建立適合自己的回饋與節奏，讓行動不再只靠一時的熱情。",
    action: "今天可以為想持續的習慣，設定一個固定時間與最小執行量。",
  },
  能力型: {
    title: "能力型卡點",
    desc: "你願意做，但目前確實還缺少某些方法或能力。這是最有機會快速前進的一種卡點，只要補上對的方法就能開始。",
    action: "今天可以列出你最想學的一個能力，並找出一個最直接的學習資源。",
  },
};

type QuizModalProps = {
  open: boolean;
  onClose: () => void;
  onOpenAi: () => void;
};

export default function QuizModal({ open, onClose, onOpenAi }: QuizModalProps) {
  const [answers, setAnswers] = useState<string[]>([]);
  const [result, setResult] = useState<string | null>(null);

  const total = QUESTIONS.length;
  const step = answers.length;
  const current = QUESTIONS[step];

  useEffect(() => {
    if (open) {
      setAnswers([]);
      setResult(null);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (type: string) => {
    const next = [...answers, type];
    setAnswers(next);
    if (next.length >= total) {
      const tally: Record<string, number> = {};
      next.forEach((t) => {
        tally[t] = (tally[t] || 0) + 1;
      });
      let best = "";
      let bestScore = -1;
      Object.keys(tally).forEach((k) => {
        if (tally[k] > bestScore) {
          bestScore = tally[k];
          best = k;
        }
      });
      setResult(best);
    }
  };

  const goBack = () => {
    if (answers.length > 0) setAnswers(answers.slice(0, -1));
  };

  const restart = () => {
    setAnswers([]);
    setResult(null);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-primary-dark/60"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <div className="relative w-full sm:max-w-lg bg-cream rounded-t-card sm:rounded-card overflow-hidden max-h-[92vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <p className="text-sm font-semibold text-ink">30 秒找出我的行動卡點</p>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-ink-soft hover:bg-ink/5 cursor-pointer"
            aria-label="關閉"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        {result ? (
          <div className="px-6 py-7 overflow-y-auto">
            <span className="inline-flex rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-semibold text-primary">
              初步整理
            </span>
            <h3 className="mt-4 text-xl md:text-2xl font-bold text-ink">
              {RESULTS[result].title}
            </h3>
            <p className="mt-4 text-sm md:text-base leading-[1.8] text-ink-soft">
              {RESULTS[result].desc}
            </p>

            <div className="mt-5 rounded-md bg-white border border-line p-4">
              <p className="text-xs font-semibold text-gold uppercase tracking-wide">
                今天可以試試
              </p>
              <p className="mt-2 text-sm leading-[1.8] text-ink">{RESULTS[result].action}</p>
            </div>

            <p className="mt-4 text-xs text-ink-soft/60">
              這只是初步整理，不是心理診斷或人格判定。
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <Button
                onClick={() => {
                  onClose();
                  onOpenAi();
                }}
                icon="ri-chat-3-line"
              >
                和 AI 幸福人才顧問聊聊
              </Button>
              <Button to="/happiness-talent-system" onClick={onClose} variant="outline">
                了解幸福人才養成系統
              </Button>
            </div>

            <button
              onClick={restart}
              className="mt-6 text-sm text-ink-soft hover:text-gold transition-colors cursor-pointer"
            >
              重新測驗
            </button>
          </div>
        ) : (
          <div className="px-6 py-6 overflow-y-auto">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-ink-soft">
                {step + 1} / {total}
              </span>
              {step > 0 && (
                <button
                  onClick={goBack}
                  className="inline-flex items-center gap-1 text-xs font-medium text-ink-soft hover:text-gold transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                  上一步
                </button>
              )}
            </div>
            <div className="mt-2.5 h-1.5 w-full bg-line rounded-full overflow-hidden">
              <div
                className="h-full bg-gold rounded-full transition-all duration-300"
                style={{ width: `${(step / total) * 100}%` }}
              />
            </div>

            <h3 className="mt-6 text-lg md:text-xl font-bold text-ink leading-[1.5]">
              {current.question}
            </h3>

            <div className="mt-5 space-y-2.5">
              {current.options.map((o) => (
                <button
                  key={o.label}
                  onClick={() => pick(o.type)}
                  className="w-full text-left rounded-md border border-line bg-white px-5 py-4 text-sm md:text-base text-ink hover:border-gold hover:bg-gold-soft/30 transition-colors cursor-pointer"
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}